'use client';

import { Suspense, useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/Toast';
import { Button, Input, Checkbox, Spinner, Modal } from '@/components/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

function EmployerSignupForm() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isKakao, setIsKakao] = useState(false);
  const [kakaoId, setKakaoId] = useState('');
  const [termsModal, setTermsModal] = useState(null);
  const [termsContent, setTermsContent] = useState('');
  const [formData, setFormData] = useState({
    // Step 1: Account info
    email: '',
    password: '',
    passwordConfirm: '',
    phone: '',
    // Step 2: Business info
    managerName: '',
    bizNo: '',
    bizName: '',
    ceoName: '',
    bizAddress: '',
    // Step 3: Bank info
    bankName: '',
    accountNo: '',
    accountHolder: '',
    // Agreements
    agreeAll: false,
    agreePrivacy: false,
    agreeTerms: false,
    agreeThirdParty: false,
    agreeMarketing: false,
  });
  const [errors, setErrors] = useState({});

  const { signup } = useAuth();
  const toast = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();

  // 카카오 프리필
  useEffect(() => {
    if (searchParams.get('from') !== 'kakao') return;
    try {
      const raw = document.cookie
        .split('; ')
        .find(c => c.startsWith('kakaoProfile='));
      if (!raw) return;
      const profile = JSON.parse(decodeURIComponent(raw.split('=').slice(1).join('=')));
      setIsKakao(true);
      setKakaoId(profile.kakaoId || '');
      setFormData(prev => ({
        ...prev,
        email: profile.email || prev.email,
        managerName: profile.name || prev.managerName,
      }));
      // 쿠키 삭제
      document.cookie = 'kakaoProfile=; path=/; max-age=0';
    } catch {
      // 파싱 실패 무시
    }
  }, [searchParams]);

  // 다음 우편번호 검색
  const openPostcode = () => {
    new window.daum.Postcode({
      oncomplete: (data) => {
        const addr = data.roadAddress || data.jibunAddress;
        setFormData(prev => ({ ...prev, bizAddress: addr }));
      },
    }).open();
  };

  // 약관 내용 로드
  useEffect(() => {
    if (!termsModal) return;
    const typeMap = { privacy: 'privacy', service: 'service', thirdParty: 'privacy' };
    const titleMap = { privacy: '개인정보 처리방침', service: '서비스 이용약관', thirdParty: '제3자 정보제공 동의' };
    fetch(`/api/common/terms/${typeMap[termsModal]}`)
      .then(r => r.json())
      .then(res => {
        if (termsModal === 'thirdParty') {
          setTermsContent(`[제3자 정보제공 동의]\n\n다새로는 서비스 제공을 위해 아래와 같이 제3자에게 개인정보를 제공합니다.\n\n1. 제공받는 자: 구직자 또는 구인자 (매칭 상대방)\n2. 제공 항목: 이름, 연락처, 사업자 정보\n3. 제공 목적: 구인·구직 매칭 및 근로계약 체결\n4. 보유 기간: 목적 달성 시까지 (최대 1년)\n\n동의를 거부할 수 있으나, 거부 시 서비스 이용이 제한됩니다.`);
        } else {
          setTermsContent(res.data?.content || '약관 내용을 불러올 수 없습니다.');
        }
      })
      .catch(() => setTermsContent('약관 내용을 불러올 수 없습니다.'));
  }, [termsModal]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleAgreeAll = (e) => {
    const { checked } = e.target;
    setFormData(prev => ({
      ...prev,
      agreeAll: checked,
      agreePrivacy: checked,
      agreeTerms: checked,
      agreeThirdParty: checked,
      agreeMarketing: checked,
    }));
  };

  const validateStep = (stepNum) => {
    const newErrors = {};

    if (stepNum === 1) {
      if (!formData.email) newErrors.email = '이메일을 입력해주세요.';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = '올바른 이메일 형식이 아닙니다.';

      if (!isKakao) {
        if (!formData.password) newErrors.password = '비밀번호를 입력해주세요.';
        else if (formData.password.length < 8) newErrors.password = '비밀번호는 8자 이상이어야 합니다.';

        if (formData.password !== formData.passwordConfirm) {
          newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
        }
      }

      if (!formData.phone) newErrors.phone = '휴대폰 번호를 입력해주세요.';
    }

    if (stepNum === 2) {
      if (!formData.managerName) newErrors.managerName = '담당자명을 입력해주세요.';
      if (!formData.bizNo) newErrors.bizNo = '사업자등록번호를 입력해주세요.';
      else if (!/^\d{10}$/.test(formData.bizNo.replace(/-/g, ''))) {
        newErrors.bizNo = '올바른 사업자등록번호 형식이 아닙니다.';
      }
      if (!formData.bizName) newErrors.bizName = '상호명을 입력해주세요.';
      if (!formData.ceoName) newErrors.ceoName = '대표자명을 입력해주세요.';
    }

    if (stepNum === 3) {
      if (!formData.agreePrivacy) newErrors.agreePrivacy = '개인정보 처리방침에 동의해주세요.';
      if (!formData.agreeTerms) newErrors.agreeTerms = '이용약관에 동의해주세요.';
      if (!formData.agreeThirdParty) newErrors.agreeThirdParty = '제3자 정보제공에 동의해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setLoading(true);

    try {
      await signup({
        email: formData.email,
        ...(isKakao ? { kakaoId } : { password: formData.password }),
        phone: formData.phone,
        role: 'employer',
        managerName: formData.managerName,
        bizNo: formData.bizNo.replace(/-/g, ''),
        bizName: formData.bizName,
        ceoName: formData.ceoName,
        bizAddress: formData.bizAddressDetail ? `${formData.bizAddress} ${formData.bizAddressDetail}` : formData.bizAddress,
        bankName: formData.bankName,
        accountNo: formData.accountNo,
        accountHolder: formData.accountHolder || formData.ceoName,
        agreePrivacy: formData.agreePrivacy,
        agreeTerms: formData.agreeTerms,
        agreeThirdParty: formData.agreeThirdParty,
        agreeMarketing: formData.agreeMarketing,
      });

      toast.success('회원가입이 완료되었습니다. 관리자 승인 후 이용 가능합니다.');
      router.push('/login');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (step < 3) {
      handleNext();
    } else {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="p-6 flex items-center gap-4 border-b bg-white">
        <button
          onClick={step > 1 ? handleBack : () => router.back()}
          className="p-2 -ml-2 hover:bg-slate-100 rounded-xl"
        >
          <ArrowLeftIcon className="w-6 h-6 text-slate-600" />
        </button>
        <h1 className="text-lg font-semibold text-slate-800">구인자 회원가입</h1>
      </div>

      {/* Progress */}
      <div className="px-6 py-4 bg-white border-b">
        <div className="flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex-1 flex items-center gap-2">
              <div className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-blue-500' : 'bg-slate-200'}`} />
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-500 mt-2">
          {step}/3 단계 - {step === 1 ? '계정 정보' : step === 2 ? '사업자 정보' : '약관 동의'}
        </p>
      </div>

      {/* Content */}
      <form onSubmit={handleFormSubmit} className="flex-1 flex flex-col">
        <div className="flex-1 p-6">
          <div className="max-w-md mx-auto space-y-4">
          {/* Step 1: Account Info */}
          {step === 1 && (
            <>
              {isKakao && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-800">
                  카카오 계정으로 가입합니다. 추가 정보를 입력해주세요.
                </div>
              )}
              <Input
                label="이메일"
                type="email"
                name="email"
                placeholder="example@email.com"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                disabled={isKakao && !!formData.email}
                required
              />
              {!isKakao && (
                <>
                  <Input
                    label="비밀번호"
                    type="password"
                    name="password"
                    placeholder="8자 이상"
                    value={formData.password}
                    onChange={handleChange}
                    error={errors.password}
                    required
                  />
                  <Input
                    label="비밀번호 확인"
                    type="password"
                    name="passwordConfirm"
                    placeholder="비밀번호 재입력"
                    value={formData.passwordConfirm}
                    onChange={handleChange}
                    error={errors.passwordConfirm}
                    required
                  />
                </>
              )}
              <Input
                label="휴대폰 번호"
                type="tel"
                name="phone"
                placeholder="010-0000-0000"
                value={formData.phone}
                onChange={handleChange}
                error={errors.phone}
                required
              />
            </>
          )}

          {/* Step 2: Business Info */}
          {step === 2 && (
            <>
              <Input
                label="담당자명"
                type="text"
                name="managerName"
                placeholder="담당자 성함"
                value={formData.managerName}
                onChange={handleChange}
                error={errors.managerName}
                required
              />
              <Input
                label="사업자등록번호"
                type="text"
                name="bizNo"
                placeholder="000-00-00000"
                value={formData.bizNo}
                onChange={handleChange}
                error={errors.bizNo}
                required
              />
              <Input
                label="상호명"
                type="text"
                name="bizName"
                placeholder="회사명"
                value={formData.bizName}
                onChange={handleChange}
                error={errors.bizName}
                required
              />
              <Input
                label="대표자명"
                type="text"
                name="ceoName"
                placeholder="대표자 성함"
                value={formData.ceoName}
                onChange={handleChange}
                error={errors.ceoName}
                required
              />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">사업장 주소</label>
                <div className="flex gap-2 mb-2">
                  <Input
                    type="text"
                    name="bizAddress"
                    placeholder="주소 검색을 클릭하세요"
                    value={formData.bizAddress}
                    readOnly
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="md"
                    onClick={() => {
                      if (!window.daum?.Postcode) {
                        const script = document.createElement('script');
                        script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
                        script.onload = () => openPostcode();
                        document.head.appendChild(script);
                      } else {
                        openPostcode();
                      }
                    }}
                  >
                    주소 검색
                  </Button>
                </div>
                <Input
                  type="text"
                  name="bizAddressDetail"
                  placeholder="상세주소 입력"
                  value={formData.bizAddressDetail || ''}
                  onChange={handleChange}
                />
              </div>
            </>
          )}

          {/* Step 3: Agreements */}
          {step === 3 && (
            <>
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                <Checkbox
                  name="agreeAll"
                  label="전체 동의"
                  checked={formData.agreeAll}
                  onChange={handleAgreeAll}
                  className="pb-3 border-b border-slate-100"
                />
                <div className="space-y-1">
                  <Checkbox
                    name="agreePrivacy"
                    label="[필수] 개인정보 처리방침 동의"
                    checked={formData.agreePrivacy}
                    onChange={handleChange}
                    error={errors.agreePrivacy}
                  />
                  <button type="button" onClick={() => setTermsModal('privacy')} className="ml-7 text-xs text-blue-500 underline">내용 보기</button>
                </div>
                <div className="space-y-1">
                  <Checkbox
                    name="agreeTerms"
                    label="[필수] 이용약관 동의"
                    checked={formData.agreeTerms}
                    onChange={handleChange}
                    error={errors.agreeTerms}
                  />
                  <button type="button" onClick={() => setTermsModal('service')} className="ml-7 text-xs text-blue-500 underline">내용 보기</button>
                </div>
                <div className="space-y-1">
                  <Checkbox
                    name="agreeThirdParty"
                    label="[필수] 제3자 정보제공 동의"
                    checked={formData.agreeThirdParty}
                    onChange={handleChange}
                    error={errors.agreeThirdParty}
                  />
                  <button type="button" onClick={() => setTermsModal('thirdParty')} className="ml-7 text-xs text-blue-500 underline">내용 보기</button>
                </div>
                <Checkbox
                  name="agreeMarketing"
                  label="[선택] 마케팅 정보 수신 동의"
                  checked={formData.agreeMarketing}
                  onChange={handleChange}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
                구인자(사업자) 가입은 관리자 승인 후 서비스 이용이 가능합니다.
                승인 완료 시 알림을 보내드립니다.
              </div>
            </>
          )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-white border-t">
          <div className="max-w-md mx-auto">
            {step < 3 ? (
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
              >
                다음
              </Button>
            ) : (
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : '가입 신청'}
              </Button>
            )}
          </div>
        </div>
      </form>

      {/* 약관 모달 */}
      {termsModal && (
        <Modal
          isOpen={!!termsModal}
          onClose={() => setTermsModal(null)}
          title={termsModal === 'privacy' ? '개인정보 처리방침' : termsModal === 'service' ? '서비스 이용약관' : '제3자 정보제공 동의'}
        >
          <div className="max-h-96 overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm text-slate-700 leading-relaxed">{termsContent}</pre>
          </div>
          <div className="mt-4">
            <Button variant="primary" size="md" className="w-full" onClick={() => setTermsModal(null)}>확인</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default function EmployerSignupPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-slate-50 flex items-center justify-center"><Spinner /></div>}>
      <EmployerSignupForm />
    </Suspense>
  );
}
